import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Params } from '@angular/router';
import { Observable, catchError, finalize, of, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';

// Composants
import { BaseComponent, SELECTED_QUERY_PARAM } from 'src/app/@shared/components/abstract/base.component';

// Services
import { AgentActionService } from 'src/app/@core/services/ai/agent-action.service';

// Modèles
import { AgentAction } from 'src/app/@shared/models/ai/agent-action.model';
import { Establishment } from 'src/app/@shared/models/partner/establishment.model';
import { Partner } from 'src/app/@shared/models/partner/partner.model';
import { LedgerEntry } from 'src/app/@shared/models/settlment/ledger-entry.model';
import { Settlement } from 'src/app/@shared/models/settlment/settlement.model';

// Utils
import { Filter } from 'src/app/@shared/utilities/models/filter';

// Thème
import { aiTag } from 'src/assets/themes/biifor/components/tag';

/**
 * Objets que l'agent écrit ou propose d'écrire, et que la carte sait ouvrir. Une adresse
 * n'en fait pas partie : le serveur ne dit pas à quelle fiche elle appartient.
 */
const ACCOUNT_MODEL = 'accounting.account';
const BANK_ACCOUNT_LINE_MODEL = 'banking.bankaccountline';
const BANK_ACCOUNT_RULE_MODEL = 'banking.bankaccountrule';
const CASH_TRANSACTION_MODEL = 'banking.cashtransaction';
const CHEQUE_MODEL = 'banking.cheque';
const DEPOSIT_MODEL = 'banking.deposit';
const EMPLOYEE_MODEL = 'human_resources.employee';
const ESTABLISHMENT_MODEL = 'partners.establishment';
const PARTNER_CONTACT_MODEL = 'partners.partnercontact';
const PARTNER_MODEL = 'partners.partner';
const PRODUCT_MODEL = 'products.product';
const PURCHASE_INVOICE_MODEL = 'purchasing.purchaseinvoice';
const QUOTATION_MODEL = 'sales.quotation';
const REMUNERATION_MODEL = 'human_resources.remuneration';
const SALES_INVOICE_MODEL = 'sales.salesinvoice';
const SELLING_INFO_RECORD_MODEL = 'sales.sellinginforecord';
const SETTLEMENT_MODEL = 'settlements.settlement';
const TGC_DECLARATION_MODEL = 'taxation.tgcdeclaration';
const OPENABLE_MODELS = [
    ACCOUNT_MODEL,
    BANK_ACCOUNT_LINE_MODEL,
    BANK_ACCOUNT_RULE_MODEL,
    CASH_TRANSACTION_MODEL,
    CHEQUE_MODEL,
    DEPOSIT_MODEL,
    EMPLOYEE_MODEL,
    ESTABLISHMENT_MODEL,
    PARTNER_CONTACT_MODEL,
    PARTNER_MODEL,
    PRODUCT_MODEL,
    PURCHASE_INVOICE_MODEL,
    QUOTATION_MODEL,
    REMUNERATION_MODEL,
    SALES_INVOICE_MODEL,
    SELLING_INFO_RECORD_MODEL,
    SETTLEMENT_MODEL,
    TGC_DECLARATION_MODEL,
];

const DATE_FORMAT = 'DD/MM/YYYY [à] HH:mm';

/**
 * Carte d'une écriture de l'agent dans le chat (#1217) : le récapitulatif toujours, et les
 * boutons tant que le serveur les accepterait — Annuler / Conserver pour une écriture
 * directe, Appliquer / Refuser pour une proposition, avec ce qu'elle écrira.
 *
 * Après un geste — réussi, refusé ou « déjà revu » —, la carte est relue et l'action
 * affichée mise à jour : c'est la même instance que porte le fil de la conversation, qui
 * reste ainsi à jour d'un écran à l'autre. `changed` prévient l'hôte qui liste les actions.
 */
@Component({
    selector: 'app-assistant-action-card',
    standalone: true,
    imports: [
        ButtonModule,
        TagModule,
    ],
    templateUrl: './assistant-action-card.component.html',
})
export class AssistantActionCardComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    public readonly aiTag = aiTag;
    public pending: boolean = false;
    /** Ligne sous le récapitulatif : qui a tranché et quand, ou jusqu'à quand la proposition attend. */
    public statusLine: string = '';
    /** Couleur de l'état ; `null` = violet, une écriture directe que personne n'a encore revue. */
    public tagSeverity: 'success' | 'secondary' | 'warn' | null = null;

    @Output() changed = new EventEmitter<AgentAction>();

    private _actionService = inject(AgentActionService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _action!: AgentAction;
    public get action(): AgentAction {
        return this._action;
    }
    @Input() public set action(action: AgentAction) {
        this._action = action;
        this._buildStatus();
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get canApply(): boolean {
        return this.action.proposed && this.action.canApply;
    }

    /**
     * Annuler / Conserver ne valent que pour une écriture appliquée : le serveur sert aussi le
     * marqueur de l'objet sur une proposition qui le vise (un devis créé par l'assistant, dont
     * il propose ensuite de changer l'objet), alors qu'un marqueur ne couvre que des écritures.
     */
    public get canKeep(): boolean {
        return this.action.processingStatus === 'applied' && !!this.action.markerId && this.action.canKeep;
    }

    /**
     * Rien à consulter quand l'objet visé n'existe pas ou plus : le serveur sert alors un
     * `target.id` nul (création proposée, suppression appliquée, création annulée, objet
     * supprimé à la main), et une action annulée n'a plus d'objet à montrer.
     */
    public get canOpenTarget(): boolean {
        const target = this.action.target;
        return !this.action.undone && !!target?.id && OPENABLE_MODELS.includes(target.model);
    }

    public get canReject(): boolean {
        return this.action.proposed && this.action.canReject;
    }

    /** Cf. `canKeep`. */
    public get canUndo(): boolean {
        return this.action.processingStatus === 'applied' && !!this.action.markerId && this.action.canUndo;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickApply(): void {
        this._confirmApply();
    }

    public onClickKeep(): void {
        this._keep();
    }

    public onClickOpenTarget(): void {
        this._openTarget();
    }

    public onClickReject(): void {
        this._reject();
    }

    public onClickUndo(): void {
        this._confirmUndo();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _apply(): void {
        this._runAndRefresh(this._actionService.apply$(this.action.id));
    }

    /**
     * Mémoïsé : recalculé à la réception de l'action et après sa relecture. Violet tant
     * qu'une écriture directe attend d'être revue, orange tant qu'une proposition attend
     * une décision, vert une fois gardée par un humain, gris une fois défaite ou écartée.
     */
    private _buildStatus(): void {
        const action = this._action;
        const reviewer = action.reviewedBy?.fullName;
        const reviewedAt = action.reviewedAt?.format(DATE_FORMAT);
        const expiresAt = action.expiresAt?.format(DATE_FORMAT);

        if (action.proposed) {
            this.tagSeverity = 'warn';
            const waiting = action.canApply ? `Demandée par ${action.user?.fullName}` : 'En attente d\'un collaborateur habilité';
            this.statusLine = expiresAt ? `${waiting}, expire le ${expiresAt}` : waiting;
            return;
        }

        if (action.processingStatus === 'applied' && !action.reviewedBy) {
            this.tagSeverity = null;
        } else if (action.undone || action.processingStatus === 'rejected') {
            this.tagSeverity = 'secondary';
        } else {
            this.tagSeverity = 'success';
        }
        const review = [reviewer ? `par ${reviewer}` : '', reviewedAt ? `le ${reviewedAt}` : ''].filter(Boolean).join(' ');
        this.statusLine = review ? `${action.processingStatusLabel} ${review}` : '';
    }

    /** Appliquer, c'est signer ce que l'agent propose — une suppression est irréversible. */
    private _confirmApply(): void {
        this.confirmAction('APPLY_AGENT_ACTION', () => this._apply());
    }

    private _confirmUndo(): void {
        this.confirmAction('UNDO_AGENT_MARKER', () => this._undo());
    }

    private _keep(): void {
        this._runAndRefresh(this.agentMarkerService.keep$(this.action.markerId!));
    }

    /**
     * Dirige l'onglet ouvert au clic vers l'objet, ou l'ouvre s'il ne l'est pas encore : la
     * conversation reste affichée là où elle était.
     */
    private _navigate(path: string, queryParams?: Params, tab?: Window | null): void {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([this.appService.clientUrl(path)], queryParams ? { queryParams } : {})
        );
        if (tab) {
            tab.location.href = url;
        } else {
            window.open(url, '_blank');
        }
    }

    /**
     * Un contact s'ouvre sur la fiche de son partenaire, où il est listé (#1245). Le serveur
     * ne sert pas le partenaire d'un contact, même sur demande : on cherche le partenaire par
     * sa relation inverse (`contactsQs`, le nom de la relation côté serveur).
     */
    private _openContact(id: number, tab: Window | null): void {
        this.subscriptions.add(
            this.partnerService.getList$({
                filters: [new Filter({ property: 'contactsQs/id', value: id })],
                selects: ['isClient'],
            }).subscribe(data => {
                const partner: Partner | undefined = data?.results?.[0];
                if (partner) {
                    this._navigate(this._partnerPath(partner), undefined, tab);
                } else {
                    tab?.close();
                }
            })
        );
    }

    /** Un établissement s'ouvre sur l'onglet Établissements de la fiche de son partenaire. */
    private _openEstablishment(id: number, tab: Window | null): void {
        this.subscriptions.add(
            this.establishmentService.get$(id, false).subscribe((establishment: Establishment | false) => {
                const partnerId = establishment ? establishment.partner?.id : null;
                if (partnerId) {
                    this._openPartner(partnerId, tab, { tab: 'establishments' });
                } else {
                    tab?.close();
                }
            })
        );
    }

    /** Un partenaire s'ouvre en client ou en fournisseur, ce que la carte ne dit pas : on le lit d'abord. */
    private _openPartner(id: number, tab: Window | null, queryParams?: Params): void {
        this.subscriptions.add(
            this.partnerService.get$(id, false).subscribe((partner: Partner | false) => {
                if (!partner) {
                    tab?.close();
                    return;
                }
                this._navigate(this._partnerPath(partner), queryParams, tab);
            })
        );
    }

    /** Un lettrage s'ouvre sur la facture qu'il solde : de vente, sinon d'achat. */
    private _openSettlement(id: number, tab: Window | null): void {
        this.subscriptions.add(
            this.settlementService.get$(id, false).subscribe((settlement: Settlement | false) => {
                if (!settlement) {
                    tab?.close();
                    return;
                }
                const entries: LedgerEntry[] = [settlement.obligation, settlement.application].filter(entry => !!entry);
                const salesInvoice = entries.find(entry => entry.sourceObjectType === 'SalesInvoice');
                const purchaseInvoice = entries.find(entry => entry.sourceObjectType === 'PurchaseInvoice');
                if (salesInvoice) {
                    this._navigate(`/sales/invoice/${salesInvoice.objectId}`, undefined, tab);
                } else if (purchaseInvoice) {
                    this._navigate(`/purchasing/invoice/${purchaseInvoice.objectId}`, undefined, tab);
                } else {
                    tab?.close();
                }
            })
        );
    }

    /**
     * Ouvre l'objet visé dans un nouvel onglet, là où l'on agit dessus, pour garder la
     * conversation sous les yeux. Ce que la carte ne dit pas se lit d'abord : si un
     * partenaire est client ou fournisseur, à quel partenaire appartient un établissement,
     * quelle facture solde un lettrage. L'onglet s'ouvre alors au clic, vide, et reçoit
     * l'objet une fois lu — un onglet ouvert après une lecture serait bloqué par le
     * navigateur — ; il se referme si la lecture ne donne rien. Un objet qui n'a pas de page
     * à lui — article, transaction bancaire, chèque, remise, opération de caisse, salarié,
     * rémunération, fiche info vente — s'ouvre dans sa fenêtre, par-dessus sa liste
     * (`?selected=`) ; un contact sur la fiche de son partenaire (#1245).
     *
     * L'objet visé se lit au clic : une transformation appliquée (copie, nouvelle version,
     * avenant, correction d'une facture) désigne le document créé, et non plus l'original,
     * une fois la carte relue (#1246).
     */
    private _openTarget(): void {
        const target = this.action.target!;
        const id = target.id!;
        switch (target.model) {
            case ACCOUNT_MODEL:
                this._navigate(`/accounting/account/${id}`);
                break;
            case BANK_ACCOUNT_LINE_MODEL:
                this._navigate('/banking/bank-account-lines', { [SELECTED_QUERY_PARAM]: id });
                break;
            case BANK_ACCOUNT_RULE_MODEL:
                this._navigate(`/banking/rule/${id}`);
                break;
            case CASH_TRANSACTION_MODEL:
                this._navigate('/banking/cash-transactions', { [SELECTED_QUERY_PARAM]: id });
                break;
            case CHEQUE_MODEL:
                this._navigate('/banking/cheques', { [SELECTED_QUERY_PARAM]: id });
                break;
            case DEPOSIT_MODEL:
                this._navigate('/banking/deposits', { [SELECTED_QUERY_PARAM]: id });
                break;
            case EMPLOYEE_MODEL:
                this._navigate('/human-resources/employees', { [SELECTED_QUERY_PARAM]: id });
                break;
            case ESTABLISHMENT_MODEL:
                this._openEstablishment(id, window.open('', '_blank'));
                break;
            case PARTNER_CONTACT_MODEL:
                this._openContact(id, window.open('', '_blank'));
                break;
            case PARTNER_MODEL:
                this._openPartner(id, window.open('', '_blank'));
                break;
            case PRODUCT_MODEL:
                this._navigate('/products', { [SELECTED_QUERY_PARAM]: id });
                break;
            case PURCHASE_INVOICE_MODEL:
                this._navigate(`/purchasing/invoice/${id}`);
                break;
            case QUOTATION_MODEL:
                this._navigate(`/sales/quotation/${id}`);
                break;
            case REMUNERATION_MODEL:
                this._navigate('/human-resources/remunerations', { [SELECTED_QUERY_PARAM]: id });
                break;
            case SALES_INVOICE_MODEL:
                this._navigate(`/sales/invoice/${id}`);
                break;
            case SELLING_INFO_RECORD_MODEL:
                this._navigate('/settings/sales/selling-info-records', { [SELECTED_QUERY_PARAM]: id });
                break;
            case SETTLEMENT_MODEL:
                this._openSettlement(id, window.open('', '_blank'));
                break;
            case TGC_DECLARATION_MODEL:
                this._navigate(`/taxation/tgc-declaration/${id}`);
                break;
        }
    }

    /** La fiche d'un partenaire se lit en client ou en fournisseur. */
    private _partnerPath(partner: Partner): string {
        return partner.isClient ? `/sales/clients/${partner.id}` : `/purchasing/vendors/${partner.id}`;
    }

    private _reject(): void {
        this._runAndRefresh(this._actionService.reject$(this.action.id));
    }

    /**
     * Joue le geste, puis relit la carte quoi qu'il arrive : le refus s'est affiché (ou
     * s'est tu pour « déjà revu »), et l'action relue dit ce qu'il en est désormais.
     */
    private _runAndRefresh(gesture$: Observable<void>): void {
        this.pending = true;
        this.subscriptions.add(
            gesture$.pipe(
                catchError(() => of(undefined)),
                switchMap(() => this._actionService.read$(this.action.id)),
                finalize(() => this.pending = false)
            ).subscribe(action => {
                if (action) {
                    this.action.hydrate(action);
                    this._buildStatus();
                }
                this.changed.emit(this.action);
            })
        );
    }

    private _undo(): void {
        this._runAndRefresh(this.agentMarkerService.undo$(this.action.markerId!));
    }
}
